import { Router } from '@angular/router';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { Component } from '@angular/core';
import { IParticipant } from '@interfaces/interfaces';
import { JSONSchema7 } from 'json-schema';
import { SessionItemData } from '@workflow/services/session-item-data';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { participantPages } from '../participants.routes';
import { ParticipantService } from '@app/services/participants/participant.service';
import { ProjectGuardService } from '@app/components/cleanui/system/Guard/project.guard';
import { WorkflowService } from '@workflow/services/workflow.service';
import { InterpolationService } from 'src/app/services/interpolation/interpolation.service';
import { ProjectService } from 'src/app/services/project/project.service';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { ExportConfig, WorkflowStatus } from '@api/projects/interfaces';
import { filter, map, switchMap, switchMapTo } from 'rxjs/operators';
import { ResolveReportDataService } from '@app/workflow/components/report-card/resolve-report-data.service';
import { ReportConfigWithVisibility, ReportFilterService } from '@app/workflow/components/report-card/report-filter.service';
import { ReportService } from '@app/services/reports/report.service';
import { cloneDeep } from 'lodash';
import { MenuService } from '@app/services/menu';
import { UnsubscribeOnDestroy } from '@app/util/lifecycle-hooks/lifecycle-hooks';
import { isNotNil } from 'ng-zorro-antd/core/util';

interface SelectExportsViewModel {
	selectedParticipant: Set<string>;
	menuReportConfigs: ReportConfigWithVisibility[];
	exportConfigs: (ExportConfig & { active?: boolean })[];
}

function isActive(workflowStatus: WorkflowStatus): (config: ExportConfig) =>ExportConfig & { active?: boolean } {
	return config => ({
		...config,
		active: config.itemId === undefined || workflowStatus.status[config.itemId]?.completed
	});
}

@Component({
	selector: 'app-participant-info',
	templateUrl: './participant-info.component.html',
	styleUrls: ['./participant-info.component.scss'],
	providers: [ProjectGuardService],
})
// eslint-disable-next-line rxjs-angular/prefer-composition
export class ParticipantInfoComponent {
	@UnsubscribeOnDestroy
	private readonly subscription = new Subscription();
	
	public readonly reportFilter$ = new BehaviorSubject<string | null>(null);
	
	public readonly participant$: Observable<IParticipant>;
	public readonly formModel$: Observable<{ schema: JSONSchema7; data: SessionItemData }>;
	public readonly report$: Observable<{ participant: IParticipant; configs: ReportConfigWithVisibility[]; data: SessionItemData; }>;
	public readonly exportsViewModel$: Observable<SelectExportsViewModel>;
	
	public backSplitButton: SplitButtonElement = {
		title: 'actions.show_participant_overview',
		icon: 'X',
		type: 'default',
		callback: () => this.navigateToParticipantList(),
	};
	
	constructor(
		private readonly router: Router,
		activeParticipantService: ActiveParticipantService,
		interpolationService: InterpolationService,
		participantService: ParticipantService,
		projectService: ProjectService,
		workflowService: WorkflowService,
		reportFilterService: ReportFilterService,
		resolveReportDataService: ResolveReportDataService,
		reportService: ReportService,
		menuService: MenuService,
	) {		
		this.participant$ = workflowService.workflowStatus$.pipe(
			switchMapTo(combineLatest([
				projectService.activeProject$.pipe(map(p => p?.project_id)),
				activeParticipantService.participant$.pipe(map(p=> p?.id))
			])),
			filter(values => values.every(isNotNil)),
			switchMap(([projectId, participantId]) =>  participantService.getParticipant$(projectId, participantId))
		);
		
		this.formModel$ = combineLatest([
			participantService.resolvedParticipantsConfigSchema$,
			this.participant$.pipe(
				map(p => new SessionItemData(p.data))
			),
		]).pipe(map(([schema, data]) => ({ schema, data })));
		
		const conf$ = combineLatest([
			projectService.activeProject$,
			this.participant$,
			reportService.reportConfigs$,
			workflowService.workflowStatus$,
		]).pipe(
			filter((values) => values.every(isNotNil)),
		);
		
		this.report$ = combineLatest([
			conf$,
			this.reportFilter$,
		]).pipe(
			switchMap(async ([[project, participant, reportConfigs, workflowStatus], filter]) => {
				if (Object.values(workflowStatus.status).every(s => !s.completed)) {
					return null;
				}
				
				const dataResolver = resolveReportDataService.createDataResolver(project.project_id, participant.id);
				let configs = await reportFilterService.resolveReportConfigs(
					reportConfigs,
					participant,
					new Date(),
					'info',
					null,
					workflowStatus,
					dataResolver);
				
				if (filter) {
					configs = configs.filter(c => c.reportId === filter);
				}
				
				return {
					participant,
					configs,
					data: dataResolver.data,
				};
			}),
		);
		
		const exportConfigs$ = projectService.exportsConfig$.pipe(
			map(cloneDeep),
			map(config => config.filter(c => c?.showInParticipantDetails))
		);
		
		const exportConfigsWithActive$ = combineLatest([
			exportConfigs$,
			workflowService.workflowStatus$
		]).pipe(
			map(([configs, workflowStatus]) => configs.map(isActive(workflowStatus)))
		);
		
		this.exportsViewModel$ = combineLatest([
			conf$,
			exportConfigsWithActive$
		]).pipe(
			switchMap(async ([[project, participant, reportConfigs, workflowStatus], exportConfigs]) => {
				const dataResolver = resolveReportDataService.createDataResolver(project.project_id, participant.id);
				const configs = await reportFilterService.resolveReportConfigs(
					reportConfigs,
					participant,
					new Date(),
					'info',
					null,
					workflowStatus,
					dataResolver,
					true);
				
				return {
					selectedParticipant: new Set<string>([participant.id]),
					menuReportConfigs: configs,
					exportConfigs: exportConfigs
				};
			}),
		);
		
		this.subscription.add(
			combineLatest([
				this.participant$,
				projectService.participantsConfig$,
			]).subscribe(([participant, config])=>{
				const name = config?.details?.info?.name;
				const template = name && interpolationService.interpolation(name);
				menuService.title = template?.interpolate(participant.data) ?? participant.id;	
			})
		);
	}
	
	private navigateToParticipantList(): void {
		this.router
			.navigate([participantPages.dashboard.base()])
			.catch(console.error);
	}
}
